import { useState } from "react";
import { Link } from "react-router-dom";
import type { Member, MemberGuarantor } from "../../../../services/types";
import { memberService } from "../../../../services";
import { useToast } from "../../../../context/ToastContext";
import { useConfirm } from "../../../../context/ConfirmContext";
import { Card } from "../../../../components/base/Card";
import { Button } from "../../../../components/base/Button";
import { TextField } from "../../../../components/base/TextField";
import { Select, type SelectOption } from "../../../../components/base/Select";
import { EmptyState } from "../../../../components/base/EmptyState";
import { Avatar } from "../../../../components/base/Avatar";

interface GuarantorsTabProps {
  member: Member;
  onUpdate: (member: Member) => void;
  isViewer: boolean;
}

const emptyForm = { name: "", relationship: "", phone: "", address: "" };

export function GuarantorsTab({ member, onUpdate, isViewer }: GuarantorsTabProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [form, setForm] = useState(emptyForm);
  const [linkedMemberId, setLinkedMemberId] = useState<string>("");
  const [linkedOptions, setLinkedOptions] = useState<SelectOption[]>([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const searchMembers = async (q: string) => {
    setMemberQuery(q);
    if (q.trim().length < 2) {
      setLinkedOptions([]);
      return;
    }
    setSearching(true);
    try {
      const results = await memberService.searchMembers(q, member.id);
      setLinkedOptions(results.map((m) => ({ value: m.id, label: `${m.fullName} (${m.memberCode})` })));
    } catch {
      setLinkedOptions([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async () => {
    setFormError(null);
    if (!form.name.trim() || !form.phone.trim()) {
      setFormError("Guarantor name and phone number are required.");
      return;
    }
    setSaving(true);
    try {
      const guarantor: MemberGuarantor = {
        id: `guar-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: form.name.trim(),
        relationship: form.relationship.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        linkedMemberId: linkedMemberId || null,
      };
      const updated = await memberService.addGuarantor(member.id, guarantor);
      onUpdate(updated);
      setForm(emptyForm);
      setLinkedMemberId("");
      setMemberQuery("");
      setLinkedOptions([]);
      toast.success("Guarantor added", `${guarantor.name} is now a guarantor for ${member.fullName}.`);
    } catch (err) {
      toast.error("Could not add guarantor", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (g: MemberGuarantor) => {
    const ok = await confirm({
      title: "Remove guarantor?",
      message: `${g.name} will be removed as a guarantor for ${member.fullName}.`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      const updated = await memberService.removeGuarantor(member.id, g.id);
      onUpdate(updated);
      toast.success("Guarantor removed", g.name);
    } catch (err) {
      toast.error("Could not remove", err instanceof Error ? err.message : "Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      {isViewer ? null : (
        <Card title="Add guarantor">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <TextField label="Name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Guarantor's full name" />
              <TextField label="Relationship" value={form.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="e.g. Brother, Spouse" />
              <TextField label="Phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" inputMode="numeric" />
              <TextField label="Address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Address" className="lg:col-span-2" />
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-foreground-700">
                Link to existing member <span className="font-normal text-foreground-500">(optional — if this person is already in the system)</span>
              </p>
              <div className="flex flex-wrap items-start gap-2">
                <TextField
                  value={memberQuery}
                  onChange={(e) => searchMembers(e.target.value)}
                  placeholder="Type at least 2 characters to search…"
                  leftIcon={searching ? "ri-loader-4-line" : "ri-user-search-line"}
                />
                <div className="w-full sm:w-80">
                  <Select
                    value={linkedMemberId || null}
                    onChange={setLinkedMemberId}
                    placeholder={linkedOptions.length ? "Select matching member" : "No matches yet"}
                    options={linkedOptions}
                  />
                </div>
              </div>
            </div>

            {formError ? <p className="text-xs text-danger-600">{formError}</p> : null}
            <div>
              <Button icon={<i className="ri-user-add-line" />} loading={saving} onClick={handleAdd}>
                Add guarantor
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card title="Guarantors" subtitle={`${member.guarantors.length} guarantor${member.guarantors.length === 1 ? "" : "s"} on record`}>
        {member.guarantors.length === 0 ? (
          <EmptyState
            icon="ri-contacts-book-line"
            title="No guarantors recorded"
            description="Guarantors help secure a member's obligations in the group."
            compact
          />
        ) : (
          <ul className="space-y-2">
            {member.guarantors.map((g) => {
              return (
                <li key={g.id} className="flex items-center gap-3 rounded-md border border-background-200 p-3">
                  <Avatar name={g.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900">{g.name}</p>
                    <p className="truncate text-xs text-foreground-500">
                      {g.relationship || "—"} · <span className="tnum">{g.phone || "—"}</span>
                      {g.address ? ` · ${g.address}` : ""}
                    </p>
                    {g.linkedMemberId ? (
                      <Link to={`/members/${g.linkedMemberId}`} className="mt-0.5 inline-block text-xs font-medium text-primary-600 hover:underline">
                        Linked to member record
                      </Link>
                    ) : null}
                  </div>
                  {!isViewer ? (
                    <Button size="sm" variant="ghost" iconOnly className="text-danger-600" icon={<i className="ri-delete-bin-line" />} onClick={() => handleRemove(g)} title="Remove" />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}